import { useState, useCallback } from 'react';
import { ZipWriter, BlobReader } from '@zip.js/zip.js/dist/zip-fs.min.js';
import * as streamSaver from 'streamsaver';

type FileKey = { key: string; fileName: string; url: string };

interface Options {
  files: FileKey[];
  zipName: string;
  maxRetries?: number;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export function useStreamingZipDownload() {
  const [progress, setProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });
  const [status, setStatus] = useState<'idle' | 'running' | 'error' | 'done'>('idle');
  const [error, setError] = useState<Error | null>(null);

  const fetchWithRetry = useCallback(async (url: string, retries = 3, backoff = 500): Promise<Response> => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res;
    } catch (err) {
      if (retries > 0) {
        await new Promise(r => setTimeout(r, backoff));
        return fetchWithRetry(url, retries - 1, backoff * 2);
      }
      throw err;
    }
  }, []);

  const download = useCallback(
    async ({ files, zipName, maxRetries = 3, onComplete, onError }: Options) => {
      setStatus('running');
      setProgress({ completed: 0, total: files.length });
      setError(null);

      try {
        const fileStream = streamSaver.createWriteStream(zipName, {
          size: undefined,
        });
        const zipWriter = new ZipWriter((fileStream as unknown) as any);

        for (let i = 0; i < files.length; i++) {
          const { key, url, fileName } = files[i];

          const res = await fetchWithRetry(url, maxRetries);
          const blob = await res.blob();

          await zipWriter.add(fileName, new BlobReader(blob));

          setProgress({ completed: i + 1, total: files.length });
        }

        await zipWriter.close();
        setStatus('done');
        onComplete?.();
      } catch (err) {
        setStatus('error');
        setError(err);
        onError?.(err);
      }
    },
    [fetchWithRetry]
  );

  return { download, progress, status, error };
}
